import { Component, Input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { FormService } from '../../../../../../services/form.service';
import { ToastService } from '../../../../../../services/toast.service';
import { PfizerPreTaskTemplate } from '../../../../../../models/forms/pfizer-pre-task-safety-plan/pfizer-form-templates/pfizer-pre-task-template.model';
import { FormDocument } from '../../../../../../models/forms/form-retrieval-response';
import { TemplateSubmissionRequest } from '../../../../../../models/forms/template-submission-request.model';

@Component({
  selector: 'app-pfizer-template-form-modal',
  templateUrl: './pfizer-template-form-modal.component.html',
  styleUrls: ['./pfizer-template-form-modal.component.css']
})
export class PfizerTemplateFormModalComponent implements OnInit {
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() templateData: FormDocument<PfizerPreTaskTemplate> | null = null;
  @Input() existingTemplateTitles: string[] = [];
  @Input() fieldMappings: Record<string, string> = {};

  form!: FormGroup;
  submitting = signal<boolean>(false);
  
  // Checkbox groupings
  requiredPermitsCheckboxes: { name: string, label: string }[] = [];
  hazardConditionsCheckboxes: { name: string, label: string }[] = [];
  controlMethodsCheckboxes: { name: string, label: string }[] = [];

  // Dynamic hazard controls
  showHazardControlIndex: Array<boolean> = [true, false, false, false, false, false, false];
  showAddHazardControlButton: boolean = true;
  hazardControlCount: number = 1;

  constructor(
    private fb: FormBuilder,
    private modalController: ModalController,
    private formService: FormService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.initializeCheckboxes();
    this.initializeForm();

    
    if (this.mode === 'edit' && this.templateData) {
      this.populateForm();
    }
  }
  
get templateTitleControl(): FormControl {
  return this.form.get('templateTitle') as FormControl;
}

get templateTitleHasError(): boolean {
  const c = this.templateTitleControl;
  return c.invalid && (c.touched || c.dirty);
}

get templateTitleErrorMessage(): string {
  const c = this.templateTitleControl;

  if (c.hasError('required')) {
    return 'Template title is required';
  }

  if (c.hasError('notUnique')) {
    return 'This template title already exists. Please choose a different name.';
  }

  return '';
}

onTemplateTitleBlur() {
  this.templateTitleControl.markAsTouched();
}


  private initializeCheckboxes(): void {
    this.requiredPermitsCheckboxes = [
      { name: 'confinedSpacePermit', label: this.fieldMappings['confinedSpacePermit'] },
      { name: 'hotWorkPermit', label: this.fieldMappings['hotWorkPermit'] },
      { name: 'lockoutTagoutPermit', label: this.fieldMappings['lockoutTagoutPermit'] },
      { name: 'otherPermit', label: this.fieldMappings['otherPermit'] }
    ];

    this.hazardConditionsCheckboxes = [
      { name: 'contactingTemperatureExtremes', label: this.fieldMappings['contactingTemperatureExtremes'] },
      { name: 'contactingElectricalCurrent', label: this.fieldMappings['contactingElectricalCurrent'] },
      { name: 'environmentalAirborneRelease', label: this.fieldMappings['environmentalAirborneRelease'] },
      { name: 'movingObjectsEquipment', label: this.fieldMappings['movingObjectsEquipment'] },
      { name: 'hazardousSubstance', label: this.fieldMappings['hazardousSubstance'] },
      { name: 'obstructionInterference', label: this.fieldMappings['obstructionInterference'] },
      { name: 'struckBy', label: this.fieldMappings['struckBy'] },
      { name: 'fallSlipTrip', label: this.fieldMappings['fallSlipTrip'] },
      { name: 'materialHandling', label: this.fieldMappings['materialHandling'] },
      { name: 'stuckAgainst', label: this.fieldMappings['stuckAgainst'] },
      { name: 'caughtInBetween', label: this.fieldMappings['caughtInBetween'] },
      { name: 'otherHazardConditions', label: this.fieldMappings['otherHazardConditions'] }
    ];

    this.controlMethodsCheckboxes = [
      { name: 'ventilationOfExposureArea', label: this.fieldMappings['ventilationOfExposureArea'] },
      { name: 'isolationOfHazardFromWorker', label: this.fieldMappings['isolationOfHazardFromWorker'] },
      { name: 'substitutionOfHazardWithLessSevereOne', label: this.fieldMappings['substitutionOfHazardWithLessSevereOne'] },
      { name: 'eliminationOfHazard', label: this.fieldMappings['eliminationOfHazard'] },
      { name: 'changeOfWorkMethods', label: this.fieldMappings['changeOfWorkMethods'] },
      { name: 'goodWorkPractices', label: this.fieldMappings['goodWorkPractices'] },
      { name: 'personalProtectiveEquipment', label: this.fieldMappings['personalProtectiveEquipment'] },
      { name: 'otherControlMethods', label: this.fieldMappings['otherControlMethods'] }
    ];
  }

  private initializeForm(): void {
    // Flat form - all fields including templateTitle
    this.form = this.fb.group({
      // Template metadata (part of PfizerPreTaskTemplate)
      templateTitle: ['', [Validators.required, this.uniqueTemplateTitleValidator.bind(this)]],
      
      // All the Pfizer Pre-Task Safety Plan fields
      subcontractor: ['MacDonald-Miller Facility Solutions'],
      jobName: [''],
      jobNumber: [''],
      taskDescription: [''],
      
      jobSteps: [''],
      identifiedHazard1: [''],
      identifiedHazard2: [''],
      identifiedHazard3: [''],
      identifiedHazard4: [''],
      identifiedHazard5: [''],
      identifiedHazard6: [''],
      identifiedHazard7: [''],
      hazardControl1: [''],
      hazardControl2: [''],
      hazardControl3: [''],
      hazardControl4: [''],
      hazardControl5: [''],
      hazardControl6: [''],
      hazardControl7: [''],
      
      confinedSpacePermit: [false],
      hotWorkPermit: [false],
      lockoutTagoutPermit: [false],
      otherPermit: [false],
      otherPermitDescription: [''],
      fireExplosionToxicHazard: [''],
      sdsReviewRequired: [''],
      
      contactingTemperatureExtremes: [false],
      contactingElectricalCurrent: [false],
      environmentalAirborneRelease: [false],
      movingObjectsEquipment: [false],
      hazardousSubstance: [false],
      obstructionInterference: [false],
      struckBy: [false],
      fallSlipTrip: [false],
      materialHandling: [false],
      stuckAgainst: [false],
      caughtInBetween: [false],
      otherHazardConditions: [false],
      otherConditions: [''],
      
      ventilationOfExposureArea: [false],
      isolationOfHazardFromWorker: [false],
      substitutionOfHazardWithLessSevereOne: [false],
      eliminationOfHazard: [false],
      changeOfWorkMethods: [false],
      goodWorkPractices: [false],
      personalProtectiveEquipment: [false],
      otherControlMethods: [false],
      otherControlMethod: [''],
      
      forepersonFirstName: [''],
      forepersonLastName: [''],
      crewMember1FirstName: [''],
      crewMember1LastName: [''],
      crewMember2FirstName: [''],
      crewMember2LastName: [''],
      crewMember3FirstName: [''],
      crewMember3LastName: [''],
      otherNotes: ['']
    });
  }

private uniqueTemplateTitleValidator(control: any) {
  if (!control.value) {
    return null;
  }

  // In edit mode, allow the current template's title
  if (this.mode === 'edit' && this.templateData?.formEntryData?.templateTitle === control.value) {
    return null;
  }

  // Case-insensitive comparison
  const titleExists = this.existingTemplateTitles.some(
    title => title.toLowerCase().trim() === control.value.toLowerCase().trim()
  );

  return titleExists ? { notUnique: true } : null;
}

  private populateForm(): void {
    if (!this.templateData?.formEntryData) return;

    const data = this.templateData.formEntryData;
    
    // Patch all values - templateTitle is part of formEntryData
    this.form.patchValue({
      templateTitle: data.templateTitle,
      subcontractor: data.subcontractor,
      jobName: data.jobName,
      jobNumber: data.jobNumber,
      taskDescription: data.taskDescription,
      jobSteps: data.jobSteps,
      identifiedHazard1: data.identifiedHazard1,
      identifiedHazard2: data.identifiedHazard2,
      identifiedHazard3: data.identifiedHazard3,
      identifiedHazard4: data.identifiedHazard4,
      identifiedHazard5: data.identifiedHazard5,
      identifiedHazard6: data.identifiedHazard6,
      identifiedHazard7: data.identifiedHazard7,
      hazardControl1: data.hazardControl1,
      hazardControl2: data.hazardControl2,
      hazardControl3: data.hazardControl3,
      hazardControl4: data.hazardControl4,
      hazardControl5: data.hazardControl5,
      hazardControl6: data.hazardControl6,
      hazardControl7: data.hazardControl7,
      confinedSpacePermit: data.confinedSpacePermit,
      hotWorkPermit: data.hotWorkPermit,
      lockoutTagoutPermit: data.lockoutTagoutPermit,
      otherPermit: data.otherPermit,
      otherPermitDescription: data.otherPermitDescription,
      fireExplosionToxicHazard: data.fireExplosionToxicHazard,
      sdsReviewRequired: data.sdsReviewRequired,
      contactingTemperatureExtremes: data.contactingTemperatureExtremes,
      contactingElectricalCurrent: data.contactingElectricalCurrent,
      environmentalAirborneRelease: data.environmentalAirborneRelease,
      movingObjectsEquipment: data.movingObjectsEquipment,
      hazardousSubstance: data.hazardousSubstance,
      obstructionInterference: data.obstructionInterference,
      struckBy: data.struckBy,
      fallSlipTrip: data.fallSlipTrip,
      materialHandling: data.materialHandling,
      stuckAgainst: data.stuckAgainst,
      caughtInBetween: data.caughtInBetween,
      otherHazardConditions: data.otherHazardConditions,
      otherConditions: data.otherConditions,
      ventilationOfExposureArea: data.ventilationOfExposureArea,
      isolationOfHazardFromWorker: data.isolationOfHazardFromWorker,
      substitutionOfHazardWithLessSevereOne: data.substitutionOfHazardWithLessSevereOne,
      eliminationOfHazard: data.eliminationOfHazard,
      changeOfWorkMethods: data.changeOfWorkMethods,
      goodWorkPractices: data.goodWorkPractices,
      personalProtectiveEquipment: data.personalProtectiveEquipment,
      otherControlMethods: data.otherControlMethods,
      otherControlMethod: data.otherControlMethod,
      forepersonFirstName: data.forepersonFirstName,
      forepersonLastName: data.forepersonLastName,
      crewMember1FirstName: data.crewMember1FirstName,
      crewMember1LastName: data.crewMember1LastName,
      crewMember2FirstName: data.crewMember2FirstName,
      crewMember2LastName: data.crewMember2LastName,
      crewMember3FirstName: data.crewMember3FirstName,
      crewMember3LastName: data.crewMember3LastName,
      otherNotes: data.otherNotes
    });

    // Update hazard control visibility
    this.updateHazardControlVisibility();
  }

  private updateHazardControlVisibility(): void {
    for (let i = 2; i <= 7; i++) {
      const hazardValue = this.form.get(`identifiedHazard${i}`)?.value;
      if (hazardValue && hazardValue.trim() !== '') {
        this.showHazardControlIndex[i - 1] = true;
        this.hazardControlCount = i;
      }
    }
    this.showAddHazardControlButton = this.hazardControlCount < 7;
  }

  addHazardControl(): void {
    if (this.hazardControlCount < 7) {
      this.hazardControlCount++;
      this.showHazardControlIndex[this.hazardControlCount - 1] = true;
      if (this.hazardControlCount === 7) {
        this.showAddHazardControlButton = false;
      }
    }
  }

  dismiss(): void {
    this.modalController.dismiss();
  }

async onSubmit(): Promise<void> {
    // Mark all fields as touched to trigger validation display
  Object.keys(this.form.controls).forEach(key => {
    this.form.get(key)?.markAsTouched();
  });
  if (this.form.invalid) {
    if (this.form.get('templateTitle')?.hasError('notUnique')) {
      this.toastService.showWarningMessage('This template title already exists. Please choose a different name.');
    } else if (this.form.get('templateTitle')?.hasError('required')) {
      this.toastService.showWarningMessage('Please enter a template title');
    } else {
      this.toastService.showWarningMessage('Please fix the errors in the form');
    }
    return;
  }

  this.submitting.set(true);

  // Get all form values
  const formValues = this.form.value;
  
  // Start with existing template data (if editing) or defaults (if creating)
  const baseData = this.mode === 'edit' && this.templateData?.formEntryData
    ? this.templateData.formEntryData  // Preserve everything from original
    : { isActive: true };  // Just set isActive for new templates
  
  // Create a proper PfizerPreTaskTemplate instance by spreading form values over base data
  const templateData = new PfizerPreTaskTemplate({
    ...baseData,      // Original data (preserves isActive, sourceDocumentId, etc.)
    ...formValues     // Form values override/update the editable fields
  });

  if (this.mode === 'create') {
    const request: TemplateSubmissionRequest<PfizerPreTaskTemplate> = {
      formType: 'pfizer-pre-task-template',
      formEntryData: templateData
    };
    
    this.formService.submitTemplate(request);
  } else {
    if (!this.templateData?.id) {
      this.toastService.showErrorMessage('Template ID not found');
      this.submitting.set(false);
      return;
    }

    this.formService.submitTemplateUpdate(
      this.templateData.id,
      'pfizer-pre-task-template',
      templateData
    );
  }

  // Dismiss modal after brief delay
  setTimeout(() => {
    this.submitting.set(false);
    this.modalController.dismiss({ success: true });
  }, 1000);
}
}